/**
 * GET /dashboard — normalized body: `userName`, `summary`, `taskStatistics`, `bugStatistics`,
 * `recentActivity`, `overloadedTeams`, `recentProjectChanges`, `activeBlockers`, `blockersOverdueCount`.
 * Response envelope: `{ success, data: { ... } }` (handled in thunk via `normalizeDashboardPayload`).
 */

import { formatDateTime } from "./dateUtils";

/**
 * Unwrap `{ data: { ... } }` from API/axios body.
 * @param {unknown} raw
 * @returns {object | null}
 */
export function normalizeDashboardPayload(raw) {
  if (raw == null || typeof raw !== "object") return null;
  const inner = raw.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) return inner;
  return raw;
}

/** Overloaded Teams panel: show when there is team or member breakdown data. */
export function shouldShowOverloadedTeams(payload) {
  const o = payload?.overloadedTeams;
  if (!o) return false;
  if (o.teams?.length) return true;
  return Object.keys(o.teamMembers ?? {}).length > 0;
}

export function shouldShowRecentProjectChanges(payload) {
  return (payload?.recentProjectChanges?.length ?? 0) > 0;
}

function activityTimeLocaleString(d) {
  const s = formatDateTime(d);
  return s === "N/A" ? "" : s;
}

/** ISO timestamps from `recentActivity[].time` → locale display. */
export function formatActivityTime(raw) {
  if (raw == null || raw === "") return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return activityTimeLocaleString(d);
}

/**
 * Same format as Recent Activity for parseable dates; otherwise the raw string (e.g. relative "4 days").
 * Use for `activeBlockers[].time` when the API may send ISO or human-readable text.
 */
export function formatActivityTimeOrString(raw) {
  if (raw == null || raw === "") return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return activityTimeLocaleString(d);
}

/** First non-empty timestamp on dashboard activity / project-change rows. */
export function pickChangeTimestamp(a) {
  if (!a || typeof a !== "object") return undefined;
  const keys = ["time", "at", "timestamp", "createdAt", "date", "occurredAt", "changedAt"];
  for (const k of keys) {
    const v = a[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return undefined;
}

/** Prefer locale `formatDateTime` for ISO; fall back to raw / relative strings. */
export function formatDashboardChangeTime(raw) {
  if (raw == null || raw === "") return "";
  const parsed = formatActivityTime(raw);
  if (parsed) return parsed;
  return formatActivityTimeOrString(raw);
}

/**
 * Prefer explicit API `initial` (1–2 chars) for project-change avatars; else derive from actor name.
 * @param {string} [actorName]
 * @param {string} [initialFromApi]
 */
export function getProjectChangeAvatarInitials(actorName, initialFromApi) {
  const raw = String(initialFromApi ?? "").trim();
  if (raw.length >= 2) return raw.slice(0, 2).toUpperCase();
  if (raw.length === 1) return raw.toUpperCase();
  return getActorInitials(actorName, "");
}

/**
 * Two-letter initials for activity avatar (e.g. "Admin User" → "AU").
 * Uses API `initial` only as a fallback when `actorName` is missing.
 * @param {string} [actorName]
 * @param {string} [initialFallback] — single letter from API
 * @returns {string}
 */
export function getActorInitials(actorName, initialFallback) {
  const s = String(actorName ?? "").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  if (parts.length === 1) {
    const p = parts[0];
    if (p.length >= 2) return p.slice(0, 2).toUpperCase();
    if (p.length === 1) return p.charAt(0).toUpperCase();
  }
  const fb = String(initialFallback ?? "").trim();
  if (fb.length >= 2) return fb.slice(0, 2).toUpperCase();
  if (fb.length === 1) return fb.toUpperCase();
  return "?";
}

/**
 * `recentActivity[]`: `{ title, name, time, actorName?, actorId?, initial? }`
 * @param {unknown} items
 */
export function mapRecentActivityItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map((a) => {
    const title = a.title ?? "";
    const name = a.name ?? "";
    const actorName = a.actorName ?? "";
    const headline = title && name ? `${title} - ${name}` : String(title || name || "").trim();
    const initials = getActorInitials(actorName, a.initial);
    return {
      title,
      name,
      headline,
      time: formatDashboardChangeTime(pickChangeTimestamp(a)),
      actorName,
      actorId: a.actorId ?? null,
      initials,
    };
  });
}

/**
 * `recentProjectChanges[]` — rich rows: `{ title, name, time, actorName?, actorId?, initial? }`
 * (same time formatting as {@link mapRecentActivityItems}).
 * Legacy: `{ text, icon?, color?, time? }` (pre-audit style).
 * @param {unknown} items
 * @returns {Array<{ id: string, headline: string, actorName: string, time: string, initials: string, isLegacy?: boolean, icon?: string, color?: string }>}
 */
export function mapRecentProjectChangeItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map((raw, index) => {
    const a = raw && typeof raw === "object" ? raw : {};
    const text = String(a.text ?? "").trim();
    const title = String(a.title ?? "").trim();
    const name = String(a.name ?? "").trim();
    const ts = pickChangeTimestamp(a);
    const hasNewShape =
      title !== "" ||
      (a.actorName != null && String(a.actorName).trim() !== "") ||
      (a.actorId != null && String(a.actorId).trim() !== "") ||
      (name !== "" && ts != null);
    const isLegacy = text !== "" && !hasNewShape;

    if (isLegacy) {
      return {
        id: `legacy-${index}-${text.slice(0, 24)}`,
        headline: text,
        actorName: "",
        time: formatDashboardChangeTime(ts),
        initials: getActorInitials("", a.initial ?? text),
        isLegacy: true,
        icon: a.icon,
        color: a.color,
      };
    }

    const actorName = String(a.actorName ?? "").trim();
    const headline =
      title && name ? `${title} - ${name}` : title || name || text || "Project change";

    return {
      id: String(a.actorId ?? ts ?? `${index}-${headline.slice(0, 32)}`),
      headline,
      actorName,
      time: formatDashboardChangeTime(ts),
      initials: getProjectChangeAvatarInitials(actorName, a.initial),
      isLegacy: false,
    };
  });
}

/** `summary`: `{ clients, projects, tasks, bugs }` */
export function getDashboardSummaryCounts(d) {
  const s = d?.summary;
  if (!s) return { clients: 0, projects: 0, tasks: 0, bugs: 0 };
  return {
    clients: s.clients ?? 0,
    projects: s.projects ?? 0,
    tasks: s.tasks ?? 0,
    bugs: s.bugs ?? 0,
  };
}
